import type { LucideIcon } from 'lucide-react';
import { BookOpen, FolderTree, Info, LayoutTemplate } from 'lucide-react';
import { painelTableShellClass } from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';

export type PainelActivityKind = 'guide-published' | 'draft' | 'category' | 'page' | 'hint';

export type PainelActivityItem = {
  id: string;
  kind: PainelActivityKind;
  /** Linha principal (ex.: título do guia). */
  title: string;
  /** Descrição curta da ação (ex.: "Guia publicado"). */
  action: string;
  /** Tempo relativo, ex. "Há 2 h". */
  relativeTime: string;
  /** Hora completa para tooltip. */
  absoluteTime?: string;
  /** Iniciais no avatar (ex. "GU", "AD"). */
  actorInitials: string;
  /** Nome do utilizador que realizou a ação. */
  actorName: string;
};

const kindConfig: Record<
  PainelActivityKind,
  { icon: LucideIcon; shell: string; iconClass: string }
> = {
  'guide-published': {
    icon: BookOpen,
    shell: 'bg-emerald-500/15 ring-emerald-400/25',
    iconClass: 'text-emerald-200',
  },
  draft: {
    icon: BookOpen,
    shell: 'bg-amber-500/12 ring-amber-400/22',
    iconClass: 'text-amber-200',
  },
  category: {
    icon: FolderTree,
    shell: 'bg-cyan-500/12 ring-cyan-400/25',
    iconClass: 'text-cyan-200',
  },
  page: {
    icon: LayoutTemplate,
    shell: 'bg-indigo-500/15 ring-indigo-400/25',
    iconClass: 'text-indigo-200',
  },
  hint: {
    icon: Info,
    shell: 'bg-sky-500/10 ring-sky-400/20',
    iconClass: 'text-sky-200',
  },
};

type PainelActivityListProps = {
  items: PainelActivityItem[];
  className?: string;
};

export function PainelActivityList({ items, className }: PainelActivityListProps) {
  return (
    <div className={cn(painelTableShellClass, 'h-full', className)}>
      <div className="border-b border-white/10 px-6 py-4">
        <h2 className="font-poppins text-base font-bold text-white">Atividade recente</h2>
        <p className="mt-0.5 font-inter text-xs text-slate-400">Últimas alterações no Help Center</p>
      </div>
      <ul className="divide-y divide-white/[0.06]">
        {items.map((item) => {
          const cfg = kindConfig[item.kind];
          const Icon = cfg.icon;

          return (
            <li key={item.id}>
              <div
                className={cn(
                  'flex gap-4 px-6 py-4 transition-colors duration-200',
                  'hover:bg-white/[0.04]'
                )}
              >
                <div className="relative shrink-0">
                  <div
                    title={item.actorName}
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-xl ring-1',
                      'font-poppins text-[11px] font-bold text-white/90',
                      'bg-gradient-to-br from-sky-600/35 to-indigo-900/40 ring-white/10'
                    )}
                  >
                    {item.actorInitials.slice(0, 2)}
                  </div>
                  <div
                    className={cn(
                      'absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-lg ring-2 ring-[#050734]',
                      cfg.shell
                    )}
                  >
                    <Icon className={cn('h-3 w-3', cfg.iconClass)} />
                  </div>
                </div>
                <div className="min-w-0 flex-1 pt-0.5">
                  <p className="font-inter text-[11px] font-medium uppercase tracking-wide text-sky-300/70">
                    {item.action}
                  </p>
                  <p className="font-poppins text-sm font-medium text-blue-50">{item.title}</p>
                  <p
                    className="mt-1 font-inter text-xs tabular-nums text-slate-400/80"
                    title={item.absoluteTime}
                  >
                    <span className="font-medium text-sky-300/85">{item.actorName}</span>
                    <span className="text-slate-500"> · </span>
                    {item.relativeTime}
                  </p>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
